import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ConfirmModal } from '../components/ConfirmModal.tsx';

export type ConfirmOptions = {
  title: string;
  /** Optional detail / impact list rendered in the modal body. */
  body?: ReactNode;
  /** Confirm-button label. Defaults to 'Confirm'. */
  confirmLabel?: string;
  /** Render the confirm button in the red danger variant. */
  danger?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

type Pending = { options: ConfirmOptions; resolve: (ok: boolean) => void };

// Provides an imperative confirm(): renders one reusable ConfirmModal and
// resolves a promise with the user's choice. Mounted once near the app root so
// any screen can `const confirm = useConfirm()` and `await confirm({...})`.
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setPending({ options, resolve })),
    [],
  );

  function settle(ok: boolean) {
    setPending((p) => {
      p?.resolve(ok);
      return null;
    });
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmModal
          open
          elevated
          title={pending.options.title}
          confirmLabel={pending.options.confirmLabel ?? 'Confirm'}
          danger={pending.options.danger ?? false}
          onConfirm={() => settle(true)}
          onClose={() => settle(false)}
        >
          {pending.options.body}
        </ConfirmModal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
